import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { noticeboardAPI } from '../../../services/api';
import { toast } from 'react-toastify';

const NoticeboardManagement = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    priority: 1,
    valid_until: '',
    is_active: true,
  });

  const noticeTypes = [
    { value: 'info', label: 'Information', color: 'primary' },
    { value: 'warning', label: 'Warning', color: 'warning' },
    { value: 'urgent', label: 'Urgent', color: 'error' },
    { value: 'update', label: 'Update', color: 'success' },
  ];

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      const response = await noticeboardAPI.getAllNotices();
      if (response.data.success) {
        setNotices(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch notices');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNotice = () => {
    setEditingNotice(null);
    setFormData({
      title: '',
      message: '',
      type: 'info',
      priority: 1,
      valid_until: '',
      is_active: true,
    });
    setOpenDialog(true);
  };

  const handleEditNotice = (notice) => {
    setEditingNotice(notice);
    setFormData({
      title: notice.title,
      message: notice.message,
      type: notice.type,
      priority: notice.priority,
      valid_until: notice.valid_until ? notice.valid_until.split('T')[0] : '',
      is_active: notice.is_active,
    });
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const submitData = {
        ...formData,
        valid_until: formData.valid_until || null,
      };

      if (editingNotice) {
        const response = await noticeboardAPI.updateNotice(editingNotice.id, submitData);
        if (response.data.success) {
          toast.success('Notice updated successfully');
        }
      } else {
        const response = await noticeboardAPI.createNotice(submitData);
        if (response.data.success) {
          toast.success('Notice created successfully');
        }
      }
      
      setOpenDialog(false);
      fetchNotices();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotice = async (id) => {
    if (window.confirm('Are you sure you want to delete this notice?')) {
      try {
        setLoading(true);
        const response = await noticeboardAPI.deleteNotice(id);
        if (response.data.success) {
          toast.success('Notice deleted successfully');
          fetchNotices();
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Delete failed');
      } finally {
        setLoading(false);
      }
    }
  };

  const getTypeChip = (type) => {
    const typeConfig = noticeTypes.find(t => t.value === type) || noticeTypes[0];
    return <Chip label={typeConfig.label} color={typeConfig.color} size="small" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No expiry';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Noticeboard Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateNotice}
          disabled={loading}
        >
          Create Notice
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Valid Until</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notices.map((notice) => (
                  <TableRow key={notice.id}>
                    <TableCell>
                      <Typography variant="subtitle2">{notice.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {notice.message.length > 50 
                          ? `${notice.message.substring(0, 50)}...` 
                          : notice.message}
                      </Typography>
                    </TableCell>
                    <TableCell>{getTypeChip(notice.type)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={notice.priority} 
                        color={notice.priority >= 3 ? 'error' : notice.priority >= 2 ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={notice.is_active ? 'Active' : 'Inactive'} 
                        color={notice.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(notice.valid_until)}</TableCell>
                    <TableCell>{notice.creator?.username || 'System'}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditNotice(notice)}
                          disabled={loading}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteNotice(notice.id)}
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {notices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">No notices found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingNotice ? 'Edit Notice' : 'Create New Notice'}
          <IconButton
            aria-label="close"
            onClick={() => setOpenDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Title"
              fullWidth
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
            
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={4}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
            />
            
            <Box display="flex" gap={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  label="Type"
                >
                  {noticeTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                label="Priority"
                type="number"
                fullWidth
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                inputProps={{ min: 1, max: 5 }}
              />
            </Box>
            
            <TextField
              label="Valid Until"
              type="date"
              fullWidth
              value={formData.valid_until}
              onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for no expiry"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={loading || !formData.title || !formData.message}
          >
            {editingNotice ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NoticeboardManagement;